import { getPipelineStatuses, getPipelineTerminalStatuses, getServicePipeline, hasLiveServicePipelines } from './servicePipelines'
import { ServicesConfig, type ServicesConfigId } from './servicesConfig'

export type AdminRuntimeSource =
    | 'root_order'
    | 'related_service'
    | 'noc_related_trigger'
    | 'informational_only'

export type AdminUpdateTarget =
    | 'order_root'
    | 'order_related_services'
    | 'noc_related_service'
    | 'read_only'

export type AdminRelatedPresentationMode =
    | 'staged'
    | 'informational'
    | 'hidden'
    | 'none'

export type AdminRelatedPatchField = 'Status' | 'Notify' | 'ScheduledAt' | 'updatedAt'

export interface LandlordServiceAdminContractItem {
    serviceId: ServicesConfigId
    title: string
    description?: string
    allowedStatuses: string[]
    terminalStatuses: string[]
    scheduledStatuses: string[]
    canUpdateRootOrder: boolean
    canUpdateRelatedService: boolean
    relatedPresentationMode: AdminRelatedPresentationMode
    runtimeSource: AdminRuntimeSource
    updateTarget: AdminUpdateTarget
    notes?: string[]
}

export const ROOT_ORDER_STATUSES = [
    'In-process',
    'Paid',
    'Providing a service',
    'Custom stage',
    'Canceled',
    'Completed',
] as const

export const GENERAL_ROOT_SERVICE_STATUSES = [
    'Request Received',
    'In Progress',
    'Completed',
    'Canceled',
] as const

const EDITABLE_RELATED_SERVICE_IDS = new Set<string>([
    'noc_generation',
    'unit_photography',
    'property_rent_valuation',
    'request_inspection',
    'inventory_checklist',
    'lease_execution',
    'tenant_screening',
    'contract_drafting',
    'utility_setup',
])

const INFORMATIONAL_PRESENTATION_SERVICE_IDS = new Set<string>([
    'tenant_screening',
    'contract_drafting',
    'utility_setup',
])

const ADMIN_OPERATIONAL_RELATED_STATUSES: Partial<Record<ServicesConfigId, string[]>> = {
    tenant_screening: [
        'Request Received',
        'Checking Records',
        'Final Review',
        'Completed',
        'Canceled',
    ],
    contract_drafting: [
        'Request Received',
        'Deposit / Payment Pending',
        'Preparing Contract',
        'Contract Initiated',
        'Sent for Signature',
        'Landlord Signed',
        'Tenant Signed',
        'Signed',
        'Completed',
        'Canceled',
    ],
    utility_setup: [
        'Request Received',
        'In Progress',
        'Utility Activation Confirmed',
        'Completed',
        'Canceled',
    ],
}

const SCHEDULED_STATUSES_BY_SERVICE_ID: Partial<Record<ServicesConfigId, string[]>> = {
    unit_photography: ['Scheduled'],
    request_inspection: ['Inspection Scheduled'],
    inventory_checklist: ['Scheduled'],
}

const ROOT_SERVICE_NOTES_WITHOUT_PIPELINE = ['No dedicated client pipeline yet; admin root-order status only.']

const unique = (values: readonly string[]): string[] => Array.from(new Set(values))

const buildContractItem = (serviceId: ServicesConfigId): LandlordServiceAdminContractItem => {
    const service = ServicesConfig.getService(serviceId)
    const hasFindingTenantWorkflowFallback = serviceId === 'find_tenant' || serviceId === 'noc_generation'
    if (!hasFindingTenantWorkflowFallback && !hasLiveServicePipelines()) {
        return {
            serviceId,
            title: service?.title ?? serviceId,
            description: service?.description,
            allowedStatuses: [],
            terminalStatuses: [],
            scheduledStatuses: [],
            canUpdateRootOrder: false,
            canUpdateRelatedService: false,
            relatedPresentationMode: 'none',
            runtimeSource: 'informational_only',
            updateTarget: 'read_only',
            notes: ['Live service pipeline config is unavailable. Status changes are disabled.'],
        }
    }
    const hasPipeline = getServicePipeline(serviceId).length > 0
    const isRelatedEditable = EDITABLE_RELATED_SERVICE_IDS.has(serviceId)
    const allowedStatuses = serviceId === 'find_tenant'
        ? [...ROOT_ORDER_STATUSES]
        : hasPipeline
            ? getPipelineStatuses(serviceId)
            : [...GENERAL_ROOT_SERVICE_STATUSES]
    const terminalStatuses = serviceId === 'find_tenant'
        ? ['Completed', 'Canceled']
        : hasPipeline
            ? getPipelineTerminalStatuses(serviceId)
            : ['Completed', 'Canceled']

    if (isRelatedEditable) {
        return {
            serviceId,
            title: service?.title ?? serviceId,
            description: service?.description,
            allowedStatuses,
            terminalStatuses,
            scheduledStatuses: SCHEDULED_STATUSES_BY_SERVICE_ID[serviceId] ?? [],
            canUpdateRootOrder: false,
            canUpdateRelatedService: true,
            relatedPresentationMode: serviceId === 'noc_generation'
                ? 'hidden'
                : INFORMATIONAL_PRESENTATION_SERVICE_IDS.has(serviceId)
                    ? 'informational'
                    : 'staged',
            runtimeSource: 'related_service',
            updateTarget: 'order_related_services',
            notes: INFORMATIONAL_PRESENTATION_SERVICE_IDS.has(serviceId)
                ? ['Admin-operational related service; client presentation may remain informational.']
                : serviceId === 'noc_generation'
                ? [
                    'Used as the same-order related trigger for Finding Tenant listing flow.',
                    'Do not write NOC status through body.Service.',
                ]
                : undefined,
        }
    }

    return {
        serviceId,
        title: service?.title ?? serviceId,
        description: service?.description,
        allowedStatuses,
        terminalStatuses,
        scheduledStatuses: SCHEDULED_STATUSES_BY_SERVICE_ID[serviceId] ?? [],
        canUpdateRootOrder: true,
        canUpdateRelatedService: false,
        relatedPresentationMode: 'none',
        runtimeSource: serviceId === 'find_tenant' ? 'noc_related_trigger' : 'root_order',
        updateTarget: 'order_root',
        notes: hasPipeline
            ? serviceId === 'find_tenant'
                ? [
                    'Paid is payment status, not a Finding Tenant stage.',
                    'NOC Initiated / Sent for Signature / Signed comes from same-order noc_generation.',
                    'Canceled / Completed may update root order status.',
                ]
                : undefined
            : ROOT_SERVICE_NOTES_WITHOUT_PIPELINE,
    }
}

export const LANDLORD_SERVICE_ADMIN_CONTRACT: readonly LandlordServiceAdminContractItem[] = ServicesConfig.config.map((service) => {
    return buildContractItem(service.id)
})

export const getLandlordServiceAdminContract = (serviceId: string): LandlordServiceAdminContractItem | undefined => {
    return ServicesConfig.getService(serviceId) != null ? buildContractItem(serviceId as ServicesConfigId) : undefined
}

export const getAdminAllowedStatuses = (serviceId: string): string[] => {
    return unique(getLandlordServiceAdminContract(serviceId)?.allowedStatuses ?? [])
}

export const isAdminScheduledStatus = (serviceId: string, status: string): boolean => {
    return getLandlordServiceAdminContract(serviceId)?.scheduledStatuses.includes(status) ?? false
}

export const isAdminTerminalStatus = (serviceId: string, status: string): boolean => {
    return getLandlordServiceAdminContract(serviceId)?.terminalStatuses.includes(status) ?? false
}

export const isAdminRelatedServiceEditable = (serviceId: string): boolean => {
    const contract = getLandlordServiceAdminContract(serviceId)
    return contract?.updateTarget === 'order_related_services' && contract.canUpdateRelatedService
}
